import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { offeringLabel } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/namedRefLabel';
import type {
  AdminRoster,
  OperatorContextRoot,
  OperatorRow,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import type { OperatorContextSelection } from '@backrs_ui/grow-ui/src/redux/operatorContext';

import { operatorHealth } from './operatorHealth';
import {
  ROSTERS_ADMIN_TAB,
  ROSTERS_ADMIN_TAB_LABELS,
  type RostersAdminTab,
} from './rostersAdminTabs';

type RostersAdminSurfaces = Readonly<{
  isInstitutionAdmin: boolean;
  tab: RostersAdminTab;
  rosters: readonly AdminRoster[];
  operators: readonly OperatorRow[];
  activeOperatorId: OperatorContextSelection | null;
  home: OperatorContextRoot | null;
  selectedInstitutionId: number | null;
  cohortSearch: string;
}>;

export const useAgentContext = ({
  isInstitutionAdmin,
  tab,
  rosters,
  operators,
  activeOperatorId,
  home,
  selectedInstitutionId,
  cohortSearch,
}: RostersAdminSurfaces) => {
  useRegisterAgentContext(
    isInstitutionAdmin ? { kind: 'tab', title: ROSTERS_ADMIN_TAB_LABELS[tab] } : null,
  );

  useRegisterAgentContext(
    isInstitutionAdmin
      ? {
          kind: 'list',
          description: 'The rosters the cohorts list is assembled from, after filtering.',
          data: {
            institution_filter: selectedInstitutionId,
            cohort_name_search: cohortSearch.trim() || null,
            rosters: rosters.map((roster) => ({
              roster_id: roster.id,
              name: offeringLabel(roster.offering),
              kind: roster.offering.kind,
              seats_used: roster.seatsUsed,
              capacity: roster.capacity,
            })),
          },
        }
      : null,
  );

  useRegisterAgentContext(
    isInstitutionAdmin && tab === ROSTERS_ADMIN_TAB.OPERATORS
      ? {
          kind: 'list',
          description: 'The operators listed on the operators tab, after searching and paging.',
          data: {
            current_operator_id: activeOperatorId,
            home: home === null ? null : { institution_id: home.id, name: home.name },
            operators: operators.map((operator) => {
              const health = operatorHealth(operator);

              return {
                institution_id: operator.id,
                name: operator.name,
                active_agreements: operator.activeAgreements,
                rosters: operator.rosters,
                over_cap: operator.overCap,
                lapsing_soon: operator.lapsingSoon,
                lapse: operator.lapse,
                is_over_cap: health.isOverCap,
                seats: health.seats,
                matched_institutions: operator.matchedInstitutions.map((institution) => ({
                  institution_id: institution.id,
                  name: institution.name,
                })),
              };
            }),
          },
        }
      : null,
  );
};
